import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pause, Play, XCircle, RefreshCw } from 'lucide-react';

import { useUploadStore } from '@/store/uploadStore';
import { uploadManager } from '@/upload/uploadManager';

/** Format bytes to human readable. */
function fmtBytes(bytes: number): string {
  if (bytes === 0) return '0 MB';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function UploadProgressSummary() {
  const { t } = useTranslation();

  const completedCount = useUploadStore((s) => s.completedCount);
  const totalFiles = useUploadStore((s) => s.totalFiles);
  const totalBytes = useUploadStore((s) => s.totalBytes);
  const uploadedBytes = useUploadStore((s) => s.uploadedBytes);
  const status = useUploadStore((s) => s.status);
  const failedCount = useUploadStore((s) => s.failedItems.length);

  const overallProgress = useMemo(() => {
    if (totalBytes === 0) return 0;
    return Math.min(100, Math.round((uploadedBytes / totalBytes) * 100));
  }, [uploadedBytes, totalBytes]);

  // Very rough ETA estimate based on a sliding average.
  // We store the start time and compute from overall throughput.
  const etaText = useMemo(() => {
    if (status !== 'uploading' || uploadedBytes === 0 || totalBytes === 0) {
      return '';
    }
    // We don't have a start timestamp in the store, so provide a byte-based summary
    const remaining = totalBytes - uploadedBytes;
    if (remaining <= 0) return '';
    // Very rough: assume average speed from what the user has seen
    // Since we don't track time here, just show remaining bytes
    return t('upload.remaining', { size: fmtBytes(remaining) });
  }, [status, uploadedBytes, totalBytes, t]);

  const isPaused = status === 'paused';
  const isComplete = status === 'complete';
  const isUploading = status === 'uploading';

  return (
    <div className="card space-y-4 p-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {isComplete
              ? t('upload.completed')
              : isPaused
                ? t('upload.paused')
                : t('upload.uploading')}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {t('upload.progressSummary', {
              completed: completedCount,
              total: totalFiles,
              uploaded: fmtBytes(uploadedBytes),
              totalSize: fmtBytes(totalBytes),
            })}
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          {failedCount > 0 && (
            <button
              type="button"
              onClick={() => uploadManager.retryFailed()}
              className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {t('upload.retryFailed', { count: failedCount })}
            </button>
          )}

          {(isUploading || isPaused) && (
            <>
              <button
                type="button"
                onClick={() =>
                  isPaused ? uploadManager.resume() : uploadManager.pause()
                }
                className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {isPaused ? (
                  <>
                    <Play className="h-3.5 w-3.5" />
                    {t('upload.resume')}
                  </>
                ) : (
                  <>
                    <Pause className="h-3.5 w-3.5" />
                    {t('upload.pause')}
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => uploadManager.cancel()}
                className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
              >
                <XCircle className="h-3.5 w-3.5" />
                {t('actions.cancel')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>{overallProgress}%</span>
          {etaText && <span>{etaText}</span>}
        </div>
        <div className="mt-1 h-2.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              isComplete
                ? 'bg-green-500'
                : isPaused
                  ? 'bg-amber-400'
                  : 'bg-primary-500'
            }`}
            style={{ width: `${overallProgress}%` }}
          />
        </div>
      </div>

      {/* Failed summary */}
      {failedCount > 0 && (
        <p className="text-xs text-red-600 dark:text-red-400">
          {t('upload.failedCount', { count: failedCount })}
        </p>
      )}
    </div>
  );
}
